/**
 * Enlaces de los correos de autenticación — `OD-0018 D-02` (`E-05`, `E-06`),
 * y el de la invitación al equipo (`E-18`, `OD-0060` D-04).
 *
 * La API los construye así (`apps/api/.../email-templates.ts`):
 *
 * ```text
 * <origen>/verificar-email#token=<token>
 * <origen>/recuperar-contrasena#token=<token>
 * <origen>/invitacion#token=<token>
 * ```
 *
 * El token va en el FRAGMENTO: el navegador no lo manda al servidor. Aquí se
 * lee una vez y se BORRA de la barra de direcciones (`replaceState`), para que
 * no quede en el historial, ni en una captura de pantalla, ni en lo que el
 * usuario copie para pedir ayuda.
 */

export type EnlaceDeCorreo =
  | { readonly tipo: "verificar-email"; readonly token: string }
  | { readonly tipo: "recuperar-contrasena"; readonly token: string }
  | { readonly tipo: "invitacion"; readonly token: string }
  | { readonly tipo: "verificar-email-roto" }
  | { readonly tipo: "recuperar-contrasena-roto" }
  | { readonly tipo: "invitacion-roto" };

const RUTAS = {
  "/verificar-email": "verificar-email",
  "/recuperar-contrasena": "recuperar-contrasena",
  "/invitacion": "invitacion",
} as const;

/** Los tokens son base64url de 32 bytes: 43 caracteres. Se acepta un margen. */
const FORMA_TOKEN = /^[A-Za-z0-9_-]{16,128}$/;

/**
 * `null` si la URL no es la de un enlace de correo.
 *
 * `...-roto` si lo es pero no trae un token con forma de token: la pantalla
 * lo dice en vez de mandar a la API algo que ya se sabe que no vale.
 */
export function leerEnlaceDeCorreo(pathname: string, hash: string): EnlaceDeCorreo | null {
  const ruta = pathname.replace(/\/+$/, "");
  const tipo = ruta in RUTAS ? RUTAS[ruta as keyof typeof RUTAS] : null;
  if (tipo === null) return null;

  const parametros = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const token = parametros.get("token");
  if (token === null || !FORMA_TOKEN.test(token)) return { tipo: `${tipo}-roto` };
  return { tipo, token };
}

/** Quita ruta y fragmento de la barra de direcciones sin recargar. */
export function olvidarEnlace(): void {
  window.history.replaceState(null, "", "/");
}
